/**
 * The art direction, as data.
 *
 * This file is the mood board's source of truth. `/moodboard.html` renders itself
 * from these values, so the reference sheet and the shader can never drift apart —
 * if you change a colour here, the mood board changes with it.
 *
 * Spec §2. Locked in docs/decisions.md; see docs/art-direction.md for the rules
 * that these numbers are the numeric expression of.
 *
 * ── The one idea worth understanding before editing anything ──────────────────
 * **The colour arc is the story arc.** Act 1 drains, the moment she gets outside
 * the game blooms, Act 3 pulls back toward grey. That arc is not a vibe to be
 * remembered by whoever is working that day — it is `chromaCeiling`, a hard
 * saturation clamp applied in the grade. Beat 7 is the only place in the game
 * where it is allowed above 0.35, and that single fact is what makes the bloom
 * land. Do not raise it elsewhere to make a screenshot look nicer.
 */

export interface ZonePalette {
  id: string;
  label: string;
  /** The one-line brief from spec §2. Kept verbatim so intent survives edits. */
  brief: string;

  /** Dominant light. Colour of the key/sun/fixture. */
  key: number;
  /** Bounce and sky fill. Also the hue that tints shadows — never neutral grey. */
  fill: number;
  /** Deepest value in the zone. Nothing in frame should go darker. */
  shadow: number;
  /** Atmospheric fog / haze colour. */
  fog: number;
  /** Optional single accent — used sparingly and always diegetic. */
  accent?: number;

  /** Representative surface colours, darkest to lightest. */
  surfaces: readonly number[];

  /**
   * Hard saturation ceiling for the zone, 0..1, applied in the grade.
   * This is the colour arc made enforceable. See the note at the top.
   */
  chromaCeiling: number;

  /**
   * The ceiling that applies instead to fragments whose hue is the zone's own
   * `accent`. Defaults to `chromaCeiling`, which is a no-op.
   *
   * Exists for exactly one situation and the cull corridor is it: a room whose
   * grade is near-monochrome and whose design calls for **one** coloured light.
   * A flat clamp cannot express that — it drains the lamp along with everything
   * else — so the ceiling is per-hue, and this is the raised one.
   *
   * Set it sparingly. Two zones with generous accent ceilings and the colour arc
   * has stopped being an arc.
   */
  accentCeiling?: number;

  /** Fog density hint for the zone, 0..1. The hall is hazed; outside is clear. */
  haze: number;

  /**
   * What the building's own filth stains toward, where water and slurry have run.
   *
   * Optional, and absent means a zone that is not soiled — the studio fixture and
   * the outside zones. See `setGrime()` in `src/render/toon.ts` for what it does;
   * the short version is that this is mixed into the albedo of every NON-fabric
   * surface, low down and on horizontals, so a room can be dirty without a single
   * hand-placed decal. It is the ROOM's equivalent of D-002's `wear`, and it is
   * deliberately a separate channel: hers is monotonic story progress, this is a
   * property of a place.
   */
  grime?: number;

  /**
   * Bedding straw, where the zone has any.
   *
   * Straw is the only warm organic colour inside the farm and it is doing
   * narrative work, not decoration: it is the one thing in beat 1 that Rosalie is
   * allowed to enjoy. It lives here rather than in the straw asset so the mood
   * board renders it and so it grades with the zone like everything else.
   */
  straw?: number;
}

/** Rosalie's own colour. She is the softest and warmest thing in every frame. */
export const ROSALIE = {
  /** Warm coral-pink plush. */
  fabric: 0xe08a76,
  /** Shaded side of the fabric — warm, never grey. */
  fabricShadow: 0x9c5a4e,
  /** Felt seam and stitching. */
  seam: 0x8a4436,
  /** Button eyes. They do not emote; they only catch light. */
  button: 0x2b2724,
  /**
   * Wear tint, mixed in as `wear` rises 0 → 1 across the game (D-002).
   * A pristine toy is a product; a worn toy is grief.
   */
  wearTint: 0x5f5347,
} as const;

/**
 * The adult plush, and it is the same toy she is — older.
 *
 * Every animal in this world is a soft toy: spec §3 gives Holly "wool like torn
 * cotton wadding" and Otto a "patchy" hide, and Rosalie is described as *the
 * softest thing in every frame she occupies* — the softest, not the only soft
 * thing. So her mother is not a real pig standing next to a toy, which would make
 * Rosalie's plushness a costume instead of a species.
 *
 * The difference between them is **albedo and proportion, never material**. Her
 * mother's pile has gone flat and grey-brown; the coral has drained out of it.
 * That is what Rosalie becomes if she stays, said in one colour, and it is the
 * only characterisation available in a beat with no words.
 *
 * Not implemented with D-002's `wear` uniform, deliberately: that uniform is
 * global and monotonic story progress (0 at Born, 1 by Act 3). Driving it here
 * would age Rosalie by standing next to her mother.
 */
export const SOW = {
  /** Drained coral — the same dye, ten years on. */
  fabric: 0xa87f74,
  fabricShadow: 0x6a463f,
  /** Her seams have been strained. Darker, and more visible than Rosalie's. */
  seam: 0x5e3229,
  button: 0x241f1d,
} as const;

/**
 * A littermate. The same mesh as Rosalie, on purpose.
 *
 * They are three days old and identical; that is exactly why an empty patch of
 * straw where one of them was reads as loss rather than as set dressing. The only
 * difference is a paler, cooler dye, so the player can tell which pig they are.
 */
export const SIBLING = {
  fabric: 0xd9a597,
  fabricShadow: 0x94665d,
  seam: 0x8a4436,
  button: 0x2b2724,
} as const;

/**
 * Otto. Spec §3: *"Enormous scarred old boar, barely fits his pen, patchy."*
 *
 * **A scar on a plush toy is a repair.** That single translation is the whole
 * character. He is not a pig with wounds; he is a toy that has been torn open and
 * sewn shut, repeatedly, by nobody in particular — coarse mismatched thread, one
 * ear replaced from some other toy, two patches worn through to the woven
 * backing. Every mark on him is evidence that he survived the thing that made it,
 * which is why he is the mentor and why he is still here.
 *
 * The dye is the same family as [[SOW]], another decade on: the coral is
 * essentially out of it. He is the DULLEST fabric in the game and he sits in the
 * warmest zone — the fattening hall's sodium key is amber, so a warm hide would
 * dissolve into the room. He reads because he is cooler and darker than the light
 * that falls on him, not because he is brighter than it.
 */
export const OTTO = {
  /** Pile gone cold and dusty. Ten years past the sow's drained coral. */
  fabric: 0x8d7a6c,
  fabricShadow: 0x574940,
  /** Worn through to the woven backing. Flatter, greyer, and it takes no light. */
  bald: 0x6d6157,
  /** Repair thread — mismatched, coarse, and the only bright thing on him. */
  thread: 0xc9a36a,
  /** The replacement ear: a colder fabric, from some other toy entirely. */
  patch: 0x7b8079,
  button: 0x241f1d,
  /** Stiffened felt tusks. Old ivory, and the only hard shape in his silhouette. */
  tusk: 0xd8cbb0,
} as const;

/**
 * A finishing pig in the fattening hall, and there are hundreds of them.
 *
 * The characterisation is that **there is none**. Rosalie, her mother and Otto
 * are each a different dye telling a different story; these are one dye, repeated,
 * with machine-neat seams and no repairs — nothing has been mended because
 * nothing here is kept long enough to need mending. A rack of identical toys is
 * the most that can be said about them, and saying it in one colour is the point.
 */
export const FINISHER = {
  fabric: 0x9d8a80,
  fabricShadow: 0x5f5049,
  /** Overlocked, even, and the same on every one of them. */
  seam: 0x6e5c53,
  button: 0x241f1d,
} as const;

/**
 * The dog. Act 1 beat 4, and the first animal in this game that is not a pig.
 *
 * **It is the best-kept toy in the world**, and that is the entire
 * characterisation. Rosalie is worn, her mother is drained, Otto is a torn toy
 * sewn shut by nobody in particular, and the finishers are a rack of identical
 * stock. The dog has **no repairs, no bald patches, no strained thread** — its
 * seams are tight and nearly invisible and its eyes are the glossiest black in
 * the game, because somebody looks after it. It is the only cared-for thing in
 * the shed, and its job is to find her.
 *
 * Do not give it red eyes, a snarl, or a black coat. It is not a monster. It is
 * frightening because it is **good at its job** and because it is the first
 * thing in this game that likes somebody.
 *
 * ── The colour is a composition decision, not a breed decision ────────────────
 *
 * A cool grey, deliberately, and it is close to the service corridor's own
 * blockwork. **The dog is the colour of the room.** It belongs there; she does
 * not, and in a corridor with nowhere to hide that reads before any mechanic
 * does. A tan farm dog would have been the second warm thing in the frame and
 * beat 4's art direction has exactly one — see the `service` zone.
 */
export const DOG = {
  /** Smooth cool grey pile. Near the corridor's own walls, on purpose. */
  fabric: 0x8e9299,
  fabricShadow: 0x5a5f66,
  /** Blaze, bib, socks and tail tip. The only near-white fabric on any animal. */
  marking: 0xd3d6d2,
  /**
   * Machine-neat and nearly invisible. Compare [[OTTO]]'s coarse mismatched
   * thread: same grammar, opposite sentence. Nothing here has ever been mended.
   */
  seam: 0x7c8087,
  /** The glossiest black in the game. New buttons on a toy that is looked after. */
  button: 0x14100f,
  nose: 0x241f1d,
  /**
   * Its collar, and it is the whole story in one strap.
   *
   * The only animal in this game wearing something a person chose for it.
   * Rosalie has nothing, Otto has repairs, the finishers have nothing. A muted
   * oxblood webbing that the service zone's 0.15 chroma ceiling will pull most
   * of the colour out of anyway — which is right. It must read as *a collar*,
   * not as an accent, and it must never compete with [[BELL]].
   */
  collar: 0x8c4b41,
  /** The buckle and the ring the lead clips to. */
  buckle: 0xb9b3a6,
} as const;

/**
 * Holly, and every colour in her is chosen against Rosalie's.
 *
 * `docs/act-2-plan.md`: *"Rosalie is round, low, warm coral and frantic. Holly is
 * tall, vertical, off-white, and calm. In every two-shot they compose as a circle
 * and a column."* That is a silhouette note, but it is a colour note too — the
 * contrast has to survive a frame where they are both small and both moving.
 *
 * So the fleece is the one **cool** off-white in the game that is not a machine's
 * (the farrowing hall's fluorescent is cold, but it is a light, not a thing). Set
 * beside 0xe08a76 it separates on hue as well as on value, which is what keeps
 * the two-shot readable when the wool shed's warm key is on both of them.
 *
 * The face and legs are much darker than the fleece, and that is not decoration:
 * it is the second half of the silhouette. A sheep drawn in one value is a cloud
 * on four sticks; the dark head on a dark bare neck is what makes the pale mass
 * read as a *body* with an animal at the front of it.
 */
export const HOLLY = {
  /** Off-white, cool, and slightly green — old wool, not a bath towel. */
  fleece: 0xd7d2c6,
  /** The shaded side of the pile. Still cool; wool does not shade warm. */
  fleeceShadow: 0x8d8a80,
  /**
   * What is under the fleece where it has been pulled out.
   *
   * She has been in wire before. The torn patch on her left flank is the story
   * told once, in a place the player will see every time she walks past — and it
   * is the same technique as Otto's bald patches, generated on the fleece's own
   * surface so it can never float free of her (D-075's whole argument).
   *
   * Flatter and darker than the pile, because a matted underlayer takes less
   * light than a lofted one.
   */
  torn: 0x9c9182,
  /** Face, ears and legs. Bare, dark, and the reason she is not a cloud. */
  bare: 0x6a6157,
  /** The muzzle and the insides of the ears — darker still. */
  muzzle: 0x4b453d,
  /** Button eyes. The same toy eyes the whole world is made of. */
  button: 0x241f1d,
  /**
   * The frayed collar.
   *
   * Deliberately drab, and the note on [[DOG]]'s collar applies here with more
   * force: this is the strap the bell hangs from, and **nothing about it may
   * compete with `BELL`**. It is a worn webbing strap somebody buckled on years
   * ago, not a ribbon.
   */
  collar: 0x6f5a4c,
  /** Cloven hooves. The darkest value on her, and the smallest. */
  hoof: 0x322d28,
} as const;

/** Holly's bell — the game's last image. The only brass in the world. */
export const BELL = 0xd6a884;

export const ZONES: readonly ZonePalette[] = [
  {
    id: 'farrowing',
    label: 'Farrowing / pens',
    brief: 'sickly fluorescent white-green, wet concrete grey, rust',
    key: 0xdfeee4,
    fill: 0x7e8a86,
    shadow: 0x39433f,
    // Raised from 0x6f7a76 on 2026-08-08, in engine, in the dressed hall.
    //
    // It was DARKER than the zone's own wall surface (0x8d918c), so distance
    // made the hall get dark instead of dissolving — the far end read as a wall
    // you had arrived at rather than a hall that keeps going. Haze is light
    // scattered INTO the eye; it has to sit above the surfaces it is veiling or
    // it is not haze, it is dusk. Plate 1's far end is a pale mist for exactly
    // this reason. D-008 was left provisional on absolute levels until a
    // dressed room existed; this is that pass.
    fog: 0xa9b4ad,
    surfaces: [0x39433f, 0x6b716e, 0x8d918c, 0x7a4a33, 0xa8aeb2],
    chromaCeiling: 0.2,
    haze: 0.15,
    // A cold olive-brown. Slurry in a hosed-out farrowing house is not the warm
    // muck of a picture-book farmyard — it is protein and disinfectant, and it
    // dries grey-green. Warmer than this and the stain reads as mud, which is
    // pastoral, which is the opposite of what this room is for.
    grime: 0x4c4a38,
    // The one warm thing in the zone, and it has to survive a 0.2 chroma ceiling
    // to be worth anything, so it is pitched light rather than saturated.
    straw: 0xbcaa79,
  },
  {
    id: 'fattening',
    label: 'Fattening hall',
    brief: 'endless amber sodium haze, deep teal shadow, dust motes',
    key: 0xf0a94e,
    fill: 0x2c4a4e,
    shadow: 0x1d3033,
    // 0x6b5330 → 0x2a3a3a on 2026-08-10, in engine, in the built hall.
    //
    // The old value was a warm brown, chosen to match the zone brief's "amber
    // sodium haze" — and it made the room ONE colour. Amber light, amber haze and
    // amber-veiled distance leave nothing for the light to be warm *against*, so
    // the sodium stopped reading as sodium and the whole hall read as sepia.
    //
    // The brief's other half is "deep teal shadow", and that is where the haze
    // belongs. Sodium at night is the only warm thing there is; everything it
    // does not reach should go cold and go away. Warm pools against cold dark is
    // the composition — the same idea as the farrowing hall's pools and gaps
    // (D-054), said in hue instead of in level.
    fog: 0x2a3a3a,
    surfaces: [0x1d3033, 0x4a4038, 0x6e7476, 0xc98b45, 0xe8b96a],
    chromaCeiling: 0.28,
    // 0.55 → 0.30 on 2026-08-10, the first time this zone was ever rendered.
    //
    // 0.55 was authored blind, against the word "hazed", with no room to put it
    // in. In the built hall it was not haze, it was **a veil at three metres**:
    // the pen wall an arm's length away was already half fog colour, every
    // surface converged on one flat mid-tone, and the room lost the only thing a
    // 24 m hall is for. The same mistake as D-008's, from the other direction —
    // that one made the far end too dark to dissolve into, this one dissolved
    // everything including the near field.
    //
    // The fog stays DARK (see `fog` below) even though the farrowing zone's had
    // to be lifted above its surfaces. That is not an inconsistency: this beat is
    // at night, and at night distance should take things away rather than turn
    // them pale. Paleness at night reads as morning.
    haze: 0.3,
    // Not the farrowing house's cold olive. That room is hosed down daily and its
    // filth is wet; this one is a dry building with an auger line overhead, and
    // what settles on every horizontal is meal dust and dried slurry. Under an
    // amber key a cold stain would read as a lighting error, so the dirt here is
    // warm and dark — the room is filthy in the same hue it is lit in.
    grime: 0x3a2e22,
    // Deliberately BRIGHTER than the farrowing hall's straw (0xbcaa79), which is
    // the only place in Act 1 that happens. Beat 3's empty pen is the game's one
    // consequence-free space (spec §5.4) and the straw in it is the reward for
    // finding it. It has a 0.28 ceiling and an amber key to work with, and it
    // should be the warmest thing in the frame that is not Rosalie herself.
    straw: 0xd0b578,
  },
  {
    id: 'service',
    label: 'Service corridor',
    brief: 'cold fluorescent white-green, painted blockwork, hosed concrete, no haze',
    /**
     * **The first cold key in the game**, and that is the zone's whole argument.
     *
     * Both halls are lit by something warm — a fluorescent batten reading amber
     * against a colder fill in the farrowing house, sodium at night in the
     * fattening hall. This room is lit by the tube itself, unapologetically, and
     * a fresh fluorescent tube is green. Not blue: blue reads as moonlight and
     * as mercy, and there is neither here.
     */
    key: 0xdfe8dc,
    /** Bounce off pale painted blockwork. Cool, and only just present. */
    fill: 0x53605c,
    shadow: 0x232b2a,
    /**
     * Almost the surface colour, because there is almost no fog to see — see
     * `haze`. It exists so that the far end of the long leg softens by a hair
     * rather than staying knife-sharp, which would read as a flat backdrop.
     */
    fog: 0x6e7a76,
    surfaces: [0x232b2a, 0x4f5a57, 0x77827d, 0x9aa39c, 0xc2c8bf],
    /**
     * Already written into the `BEATS` table below before this zone existed, and
     * kept: 0.15 sits between the fattening hall's 0.28 and the cull corridor's
     * 0.08. Beat 4 is where the colour starts draining out of Act 1, and the
     * arc is supposed to be felt as a slope rather than as a cut.
     */
    chromaCeiling: 0.15,
    /**
     * **Almost none, and it is the horror.**
     *
     * Both halls dissolve their far ends and both use it to mean "endless". This
     * room does not get to: you can see all the way to the corner and all the way
     * to the door, and there is nowhere in between to be. No haze means no soft
     * cover, which means the cover is the three bays, which means the cover is a
     * clock — the art direction and the stealth design are the same decision
     * here, exactly as the fattening hall's darkness was both the look and the
     * light budget.
     */
    haze: 0.06,
    /**
     * Hosed daily, so the filth is thin, wet and cold — and it is the LOW value
     * in the game, not a high one. A service passage is the one part of this
     * building that gets cleaned, because people walk down it.
     *
     * That is the point of the room. It is the first clean place Rosalie has ever
     * been, and it is clean because it is not for animals.
     */
    grime: 0x33403c,
  },
  {
    id: 'cull',
    label: 'Cull corridor',
    brief: 'desaturated to near-monochrome, one red light source, frosted plastic',
    key: 0xb9bcbd,
    fill: 0x4a4d4f,
    shadow: 0x1f2224,
    fog: 0x3a3d3f,
    accent: 0xc02b1f,
    surfaces: [0x1f2224, 0x4a4d4f, 0x8e9294, 0xb9bcbd, 0xcfd3d4],
    // The lowest in the game. This corridor is the centre of gravity, and it is
    // the floor the bloom in beat 7 is measured against.
    chromaCeiling: 0.08,
    /**
     * And the one exemption, which is the whole reason `accent` exists here.
     *
     * 0.08 applied flat makes a saturated red lamp into a slightly warm white,
     * and beat 5's first frames were exactly that: a near-monochrome room whose
     * single authored colour had been graded out of it (session 23). The ceiling
     * is per-hue now — see `accentDirection` in `render/post/stack.ts` — and this
     * is the only zone in the game that raises it.
     */
    accentCeiling: 0.62,
    haze: 0.35,
  },
  {
    id: 'yard',
    label: 'Loading yard',
    brief: 'night blue, headlight yellow, rain sheen, lightning-white flashes',
    key: 0xffd98a,
    fill: 0x24334d,
    shadow: 0x121a29,
    fog: 0x1c2740,
    accent: 0xf2f6ff,
    surfaces: [0x121a29, 0x2b3340, 0x5a6472, 0x8b93a1, 0xffd98a],
    chromaCeiling: 0.22,
    haze: 0.45,
  },
  {
    id: 'outside',
    label: 'Outside / fields',
    brief: 'first saturated colour in the game — wet green, dawn gold, huge sky',
    key: 0xffd27a,
    fill: 0x8fc0d9,
    shadow: 0x3a5742,
    fog: 0xcfe3ea,
    surfaces: [0x3a5742, 0x4f8f52, 0x86bb63, 0xa9d4e8, 0xffd27a],
    // The bloom. The only value above 0.35 in the game. Beat 7 earns it by
    // being the first thing the player sees after the cull corridor's 0.08.
    chromaCeiling: 0.62,
    haze: 0.1,
  },
  {
    id: 'wool-shed',
    label: 'Wool shed',
    brief: 'low sun through board gaps, dry timber, lanolin cream — the first warm room that is not a machine\u2019s',
    /**
     * **Every warm room before this one is warm because a machine is on.**
     *
     * The fattening hall's amber is sodium; the service corridor's green is a
     * tube; the cull corridor's red is a lamp somebody switched on to work by.
     * A key that comes through gaps in a wall nobody maintains is the first
     * light in the game that is not owned by the farm, and that is the whole
     * argument of Act 2's one new interior (`docs/act-2-plan.md`).
     *
     * So it is daylight-warm rather than lamp-warm: pale and slightly pink,
     * where sodium (0xf0a94e) is orange and saturated. Pitched close enough to
     * white that the wool can be the brightest thing in frame without the key
     * having to be — the room is lit by afternoon, not by a fitting.
     */
    key: 0xf2d7a0,
    /**
     * Cool, and it is load-bearing rather than decorative.
     *
     * The one thing a warm room needs is something to be warm AGAINST — the
     * lesson the fattening hall's fog cost a session to learn (see its `fog`
     * above). The open door and the gable gap admit sky, so everything the sun
     * slats miss is filled by it, and the shadow side of a bale goes blue-grey
     * while its lit side goes cream. That difference is the picture.
     */
    fill: 0x7c8f95,
    /** Warm brown-black. Nothing in a timber building shades to neutral. */
    shadow: 0x2b2119,
    /**
     * Lifted well above the dark boards, which is D-008's rule applied to a room
     * that is not deep enough to need it for distance.
     *
     * The shed is 9 m; nothing in it dissolves. What the haze is here for is the
     * **shafts** — dust hanging in the slats of light through the wall boards is
     * the room's signature image, and dust lit from the side is brighter than
     * the timber it hangs in front of. A fog darker than the boards would make
     * the shafts read as smoke.
     */
    fog: 0x8a7458,
    /**
     * The bell, and it is `BELL` itself rather than a second brass.
     *
     * The zone accent is what `accentDirection` in `render/post/stack.ts` keys
     * the per-hue ceiling on, so an accent that is *nearly* the bell would grade
     * the bell as if it were an ordinary surface. This file's own rule is to
     * render the source of truth rather than copy it; a hand-typed 0xc9a227 next
     * to a declared `BELL` is exactly the drift that rule exists to stop.
     */
    accent: BELL,
    surfaces: [0x2b2119, 0x5a4230, 0x8a6a45, 0xd8cdb4, 0xefe0c2],
    /**
     * 0.30, and the arc is why it is not higher.
     *
     * Act 1 drains to the cull corridor's 0.08; beat 7 blooms to 0.62; Act 3
     * pulls back to grey. Act 2 lives between, and this room is its warmest
     * point. Above 0.35 it would compete with the bloom, and the bloom is a
     * once-per-game event that everything else is measured against — the note at
     * the top of this file is explicit that beat 7 is the only place allowed
     * past it. Below about 0.25 the timber and the wool converge on one
     * mid-brown and the room stops being the relief it exists to be.
     *
     * No `accentCeiling`. 0.30 is generous enough to carry a brass bell, and the
     * cull corridor's exemption is only defensible while it stays the only one.
     */
    chromaCeiling: 0.3,
    haze: 0.18,
    /**
     * Dry, warm and pale — lanolin and chaff, not slurry.
     *
     * The farrowing house stains cold olive because it is hosed; the fattening
     * hall stains dark brown because it is dusty and lit amber. This building is
     * neither washed nor worked: what settles here is greasy wool dust off the
     * bales, and it lightens a surface rather than darkening it. It is still
     * grime — the shed is filthy — but it is the only filth in the game that is
     * the same colour as the animal that made it.
     */
    grime: 0x6a5a3e,
    /**
     * Brighter than either hall's, and for the same reason beat 3's is brighter
     * than beat 1's: this is the room where nothing is being asked of her.
     *
     * Beat 9 ends on a rest scene with no objective and no watcher (S-8), and the
     * bedding under Holly is the only thing in it the player can go and lie in.
     * Under a 0.30 ceiling and a near-white key it should be the warmest value in
     * frame that is not Rosalie.
     */
    straw: 0xdcc07f,
  },
];

export function getZone(id: string): ZonePalette | undefined {
  return ZONES.find((z) => z.id === id);
}

/**
 * The colour arc across the game, beat by beat (spec §7).
 *
 * `chroma` is the grade's saturation ceiling at that beat. Read down the column:
 * that shape — long drain, one spike, slow withdrawal — is the story.
 */
export interface ArcPoint {
  beat: number;
  act: 1 | 2 | 3;
  title: string;
  zone: string;
  chroma: number;
  /**
   * D-002's wear channel, 0..1 — how used she looks DURING this beat.
   *
   * *"A pristine toy is a product; a worn toy is grief"* (spec §2), and D-002
   * calls this **"the primary device that makes the plush read as serious"** and
   * says implement it in M0b. `setWear()` and `getWear()` have existed in
   * `src/render/toon.ts` since then and **nothing called either for six beats**;
   * the uniform was 0 in every frame of every shipped beat. What hid it is that
   * the mood board renders the RAMP, so the ramp was looked at and the pig was
   * not (D-116).
   *
   * Read down this column: it is **monotonic**, like the story. She never gets
   * cleaner. Nothing in the game resets it, no beat lowers it, and Act 3's pull
   * back toward grey is the chroma doing that and not this.
   *
   * The column is the value *during* a beat, and `main.ts` sets it once at the
   * beat boundary. **Beat 7 is the single exception and it is the exception the
   * channel exists for**: it boots at beat 6's 0.40 and ramps to its own 0.55 at
   * the ditch, in the middle of a beat that then holds a camera on her for ninety
   * seconds in full saturated colour. See `OutDirector`'s `WEAR_RAMP`.
   *
   * If the wear ever reads as *dirt* rather than as *grief*, the lever is the
   * pile length and the matting — **never** the seams, the button eyes or the
   * roundness (D-002).
   */
  wear: number;
  note: string;
}

export const COLOUR_ARC: readonly ArcPoint[] = [
  { beat: 1, act: 1, title: 'Born', zone: 'farrowing', chroma: 0.2, wear: 0.00, note: 'Baseline. Already joyless.' },
  { beat: 2, act: 1, title: 'The Weakest Latch', zone: 'farrowing', chroma: 0.18, wear: 0.04, note: '' },
  { beat: 3, act: 1, title: 'Otto', zone: 'fattening', chroma: 0.28, wear: 0.14, note: 'Warm, but it is sodium, not sun.' },
  { beat: 4, act: 1, title: 'The Service Corridor', zone: 'service', chroma: 0.15, wear: 0.20, note: 'The first cold key. Nowhere in it she is supposed to be.' },
  { beat: 5, act: 1, title: 'The Cull Corridor', zone: 'cull', chroma: 0.08, wear: 0.32, note: 'The floor. Near-monochrome.' },
  { beat: 6, act: 1, title: 'The Loading Yard', zone: 'yard', chroma: 0.22, wear: 0.40, note: 'Colour returns as violence: headlights, lightning.' },
  { beat: 7, act: 1, title: 'Out', zone: 'outside', chroma: 0.62, wear: 0.55, note: 'THE BLOOM. Hold it long. She does not celebrate; she looks back.' },
  { beat: 8, act: 2, title: 'Blackout', zone: 'fattening', chroma: 0.3, wear: 0.58, note: 'Outside colour carried back in — the farm looks different now.' },
  // 'outside' at 0.45 -> 'wool-shed' at 0.30 on 2026-08-28, when the room this
  // beat is actually set in was built. The old row was authored before Act 2 had
  // a plan: it read "beat 9 is after beat 7, so it is outdoors and bright". Beat
  // 9 is an interior — a small timber shed — and the whole point of it is that
  // it is warm without being the bloom. 0.45 next to beat 7's 0.62 would have
  // made the arc a plateau rather than a peak. See the `wool-shed` zone above.
  { beat: 9, act: 2, title: 'Wool and Wire', zone: 'wool-shed', chroma: 0.3, wear: 0.63, note: 'The first warm room that is not a machine. Nothing is asked of her in it.' },
  { beat: 10, act: 2, title: 'Twelve Out', zone: 'outside', chroma: 0.38, wear: 0.68, note: 'Moonlit. Saturated but cold.' },
  { beat: 11, act: 3, title: 'The Tally', zone: 'farrowing', chroma: 0.2, wear: 0.72, note: 'The withdrawal begins.' },
  { beat: 12, act: 3, title: 'Dawn Transport', zone: 'yard', chroma: 0.24, wear: 0.80, note: '' },
  { beat: 13, act: 3, title: 'It fails', zone: 'yard', chroma: 0.16, wear: 0.88, note: '' },
  { beat: 14, act: 3, title: 'Ending', zone: 'outside', chroma: 0.18, wear: 0.94, note: 'Dawn gold drains to grey. The bell is the last warm thing in frame.' },
];

/**
 * Toon ramp (spec §2: 3-band, hand-painted gradient, NOT a hard cel step).
 *
 * Bands are soft-edged. The shadow band is tinted toward the zone's `fill` hue —
 * warm bounce in shadow is most of what separates "painterly" from "flat vertex
 * colour", and it is the single easiest thing to get wrong.
 */
export const TOON_RAMP = {
  /** Lambert thresholds between the three bands. */
  thresholds: [0.32, 0.62] as const,
  /** Softness of each threshold. Never 0 — a hard step reads as cel, not paint. */
  softness: [0.09, 0.12] as const,
  /** How far the shadow band tints toward the zone fill hue, 0..1. */
  shadowFillMix: 0.45,
  /** Highlight desaturation, so the light band reads as paint rather than glare. */
  highlightDesaturation: 0.18,
} as const;

/** Ink outlines (spec §2: soft, with slight thickness variation to suggest ink). */
export const OUTLINE = {
  /** Pixel width range. Variation is what suggests a drawn line. */
  widthRange: [1.0, 2.2] as const,
  /** Outline colour is the zone shadow, pushed darker — never pure black. */
  shadowDarkenFactor: 0.6,
} as const;

/** Screen-space paper (spec §2: paper grain + subtle canvas weave, low opacity). */
export const PAPER = {
  grainOpacity: 0.08,
  weaveOpacity: 0.04,
  /** Grain is fixed to the screen, not the world — it is the page, not a texture. */
  screenSpace: true,
} as const;

/**
 * The chapter card, between beats.
 *
 * Spec §7: *"Chapter cards are a **single hand-lettered image** — a bell, a
 * latch, a tally mark — never a sentence."* Two colours and nothing else, which
 * is the whole of the constraint that keeps them from becoming a title screen.
 *
 * ── Why it is chalk on slate and not ink on paper ───────────────────────────
 *
 * The obvious reading of "hand-lettered" is the game's own register: dark ink on
 * the warm paper the whole grade already sits on. It was wrong for one physical
 * reason. **A card arrives between two dark rooms.** A page of paper white
 * dropped into that gap is a flash in a game whose brightest surface so far is a
 * heat lamp, and it would be the loudest thing in Act 1 by a wide margin.
 *
 * So it is inverted: a near-black warm ground and a pale mark on it. The
 * building is full of chalked slates — pen cards, dosing boards, the tally by
 * the door — so the register is also somewhere the game has already been.
 *
 * `ground` is not `#000`, for the reason `OUTLINE.shadowDarkenFactor` exists:
 * nothing in this game's frame is pure black, and a card that was would read as
 * the fade overlay failing to clear rather than as a page.
 */
export const CHAPTER_CARD = {
  /** Warm near-black. Sits a hair above the fade so the card is visibly a THING. */
  ground: 0x141110,
  /** Chalk. The same warm off-white the paper grain is made of. */
  ink: 0xd6cec1,
  /** Seconds: the mark drawing itself on, holding, and going. */
  timing: { in: 1.3, hold: 1.9, out: 1.1 },
} as const;

/**
 * Her map (spec §5.3), and it is the same chalk and the same slate as the card.
 *
 * ── Why it borrows `CHAPTER_CARD`'s pair instead of having its own ──────────
 *
 * The card's own comment argues it and the argument transfers exactly: *"a page
 * of paper white dropped into that gap is a flash in a game whose brightest
 * surface so far is a heat lamp."* The map is raised **inside** a dark building,
 * with the room still visible behind it, so the same physics apply with less
 * mercy — a white sheet here would blind the player for the two seconds after
 * they let go of the key.
 *
 * It is also true of the fiction. This building is full of chalked slates: pen
 * cards, dosing boards, the tally by the door. Her notes being in the same medium
 * as the farm's paperwork is the joke the whole feature is built on, and beat 11
 * — *The Tally* — is where it stops being funny.
 *
 * ── The wash and the marks are one material used two ways ──────────────────
 *
 * Not two media. **The wash is the side of the chalk and the marks are its
 * edge**, which is a distinction anybody who has held one recognises without
 * being told. Soft, wide and low-opacity is a memory of a PLACE — where she
 * walked. Thin, opaque and deliberate is a memory of a PATTERN — what she
 * watched. That contrast carries the whole legibility of the sheet, and it is the
 * half that has to survive being looked at on a phone (D-125).
 *
 * Widths are in **pixels**, not metres, and applied with `vector-effect:
 * non-scaling-stroke`. A pen has a physical width; a route across a big yard is
 * not drawn with a fatter line than one across a pen. It also means a farm she
 * has walked a lot of, drawn small, correctly becomes a smudge.
 */
export const MENTAL_MAP = {
  /** The slate. The card's ground, at less than full opacity — see `groundAlpha`. */
  ground: CHAPTER_CARD.ground,
  /**
   * How much of the room the sheet hides. NOT 1, and that is the design.
   *
   * Raising the map has to cost something: the world does not stop while it is
   * up (`docs/beat-8-plan.md` rule 4), so the price of reading her notes is that
   * she cannot see the alley. Fully opaque would make it a menu; fully
   * transparent would make it a HUD element with no cost at all.
   */
  veilAlpha: 0.55,
  /** The slate's rim. A slate has a thickness; a hole cut in the screen does not. */
  slateEdge: 0x2b2622,
  slateEdgePx: 3,
  /** What is left of the room behind it. Costs no draw calls — it is `backdrop-filter`. */
  backdropGrey: 0.9,
  backdropDim: 0.5,
  // The wash — the side of the chalk, where she walked — is no longer drawn
  // (D-239): blind viewers read it as a stain, dirt or blood in every round.
  // The walls below carry where she has been.
  /** The edge of the chalk: what she watched. The card's own ink. */
  mark: CHAPTER_CARD.ink,
  markWidthPx: 2.2,
  // Her marker was a cross, dimmed so it would be the last thing the eye
  // reached. Blind, it was not reached at all, or read as danger (D-238).
  /** Her face is full chalk: it is the one mark a player has to find first (D-238). */
  self: CHAPTER_CARD.ink,
  selfWidthPx: 2.4,
  /** Her face's radius as a share of the sheet's longer side. */
  selfSheetFraction: 0.034,
  /** A watched thing's glyph radius, same measure (D-239). Larger than her face (3.4 %): at 2.6 % beat 2's worker was a speck under his own route. */
  glyphSheetFraction: 0.04,
  /** Her own trail (D-241): round dots, faint, under everything. */
  trailDash: '0.1 7',
  trailWidthPx: 3,
  trailOpacity: 0.45,
  /** Her walls (D-238): thinner and softer than a mark, because they are the room and not a thing in it. */
  wallWidthPx: 2,
  wallOpacity: 0.9,
  /** How far a wall stroke may run past its end, and how far off true it may sit. Metres. */
  wallOvershootMetres: 0.35,
  wallWobbleMetres: 0.18,
  /**
   * **His hand** (beat 11, `docs/beat-11-plan.md` — *the hand is the difference,
   * and it is a parameter*). Pencil on paper where hers is chalk on slate: a
   * pale ground, a thin hard graphite line, no wash. The same renderer draws
   * both; only this block and the jitter differ.
   */
  his: {
    ground: 0xd8d0bf,
    ink: 0x3b3834,
    /** Her line on his sheet: the same graphite, lighter, because he traced it. */
    route: 0x4a443d,
    widthPx: 1.1,
    /** Her line is the thing on his sheet the player must find, so it is the heaviest stroke on it. */
    routeWidthPx: 1.6,
    routeDash: '5 4',
    /** Margin dates, in metres of sheet: his figures are small and regular. */
    dateHeightMetres: 2.4,
  },
  /** Spec §5.3, verbatim: *"patrol routes as dashed lines"*. Pixels, per the note above. */
  routeDash: '7 6',
  /** A drawn door box, and the baseline a timing's ticks stand on. Metres. */
  doorMetres: 1.1,
  /**
   * How far a remembered route bends away from where the thing actually walked. Metres.
   * 0.35 until D-239: a round walked out and back came apart into two lines,
   * and blind viewers read the wedge as *"a vision cone"*.
   */
  routeWobble: 0.12,
  /** White space around the drawing. It is a drawing on a surface, not a texture. */
  marginMetres: 2.2,
  /**
   * The smallest the sheet is ever drawn at, in metres of world.
   *
   * Without a floor, three cells of walking fill the screen and her first look
   * at her own notes is a giant scribble — the drawing would be zoomed to
   * whatever she happens to know, which makes knowing very little look like
   * knowing a lot. With one, early knowledge is a small mark on a large page and
   * grows to fill it, which is the honest picture and also the one that rewards
   * walking the room.
   */
  minExtentMetres: 16,
  /** Seconds of hand: the ground she walked, then the things she watched. */
  washSeconds: 0.6,
  markSeconds: 0.55,
  /** Seconds for the sheet itself to arrive and to go. Going is slower than arriving. */
  fadeIn: 0.16,
  fadeOut: 0.24,
} as const;

/**
 * D-002's wear at a given beat, for `setWear()`. See `ArcPoint.wear`.
 *
 * A function rather than a lookup at the call site because the failure it
 * prevents is the one this channel has already had: **the arc is the only place
 * the value is written down**, and a `main.ts` that hard-coded 0.4 for beat 6
 * would be a second statement of the ramp that agrees with this one until
 * somebody edits one of them. That is exactly how `setWear()` came to have zero
 * callers while a carefully authored uniform sat at 0 for six beats.
 *
 * Beats outside the table — `?beat=0`, which is the farrowing hall with no beat,
 * and every workbench entry — get **0**. That is correct rather than defensive:
 * beat 0 is what the controller and perf specs boot, `golden-trace.spec.ts` is
 * downstream of anything that changes frame content, and a measuring instrument
 * whose subject is worn to some story position is measuring the wrong pig.
 */
export function wearForBeat(beat: number): number {
  return COLOUR_ARC.find((point) => point.beat === beat)?.wear ?? 0;
}

/** The authored grade ceiling for a beat, or null when the table has no story row. */
export function chromaForBeat(beat: number): number | null {
  return COLOUR_ARC.find((point) => point.beat === beat)?.chroma ?? null;
}

/**
 * A zone with its colour drained toward grey: every authored colour mixed
 * `amount` of the way to its own luminance, and the grade's ceiling set to
 * `ceiling`. Derived, never restated — the destination of beat 14's drain is
 * `outside` with the colour taken out of it, and a hand-authored grey zone
 * would be a second statement of that field that agrees until someone edits
 * the first.
 *
 * `accent` is the one hue the grade still lets through at the SOURCE zone's
 * ceiling (the cull corridor's per-hue exemption, D-094): beat 14 passes
 * `BELL`, and that is what "the bell is the last warm thing in frame" is made
 * of.
 */
export function drainZone(zone: ZonePalette, options: { amount: number; ceiling: number; accent: number }): ZonePalette {
  const drain = (hex: number): number => {
    const r = (hex >> 16) & 0xff;
    const g = (hex >> 8) & 0xff;
    const b = hex & 0xff;
    const y = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    const mix = (c: number): number => Math.round(c + (y - c) * options.amount);
    return (mix(r) << 16) | (mix(g) << 8) | mix(b);
  };
  return {
    id: `${zone.id}-drained`,
    label: `${zone.label}, drained`,
    brief: `${zone.brief} — drained to grey`,
    key: drain(zone.key),
    fill: drain(zone.fill),
    shadow: drain(zone.shadow),
    fog: drain(zone.fog),
    surfaces: zone.surfaces.map(drain),
    chromaCeiling: options.ceiling,
    accent: options.accent,
    accentCeiling: zone.chromaCeiling,
    haze: zone.haze,
  };
}

/**
 * Beat 14's end state: dawn gold drained to grey, the brass held. The row's own
 * chroma is the ceiling it lands on (`COLOUR_ARC`, 0.18).
 */
export function endingZone(): ZonePalette {
  const outside = getZone('outside');
  if (outside === undefined) throw new Error('palette: the outside zone is missing');
  return drainZone(outside, { amount: 0.78, ceiling: chromaForBeat(14) ?? 0.18, accent: BELL });
}
